import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';

import { middle } from './helpers';
import { VAULTS_NS as NS } from './constants';

const EMPTY = '—';
/** How long the copy control shows its checkmark. */
const COPIED_FOR_MS = 1500;
const ID = { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } as const;

/**
 * The vault account's own HyperCore address (C-06 `hlAddress`): where subscriptions and the manager's stake are sent,
 * never the manager's address. Cut in the middle in a list cell (`0x5f2A…7a31`), whole where there is room (`full`),
 * the whole value on hover either way, and copied whole. "—" with no copy control while there is none: the account is
 * not provisioned yet (Sami, 2026-10-02: "bổ sung vào cả List và Detail").
 */
export const VaultAddress = ({
  address,
  full = false,
  muted = false,
}: {
  address?: string | null;
  full?: boolean;
  /** An application that no longer takes transfers (FAILED / CANCELLED): shown, greyed. */
  muted?: boolean;
}) => {
  const translate = useTranslate();
  const { copy } = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  // The checkmark is a confirmation, not a state: it goes back to the copy control after a moment.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!address) {
    return (
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
        {EMPTY}
      </Typography>
    );
  }
  const color = muted ? 'text.disabled' : full ? 'text.primary' : 'text.secondary';
  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Tooltip title={address}>
        <Typography variant={full ? 'body2' : 'caption'} component="span" sx={{ ...ID, color }}>
          {full ? address : middle(address, 6, 4)}
        </Typography>
      </Tooltip>
      <Tooltip title={translate(copied ? `${NS}.addressCopied` : `${NS}.copyAddress`)}>
        <IconButton
          size="small"
          aria-label={translate(copied ? `${NS}.addressCopied` : `${NS}.copyAddress`)}
          onClick={async (event) => {
            // The cell sits in a clickable row (Vault Management) — copying must not open it.
            event.stopPropagation();
            if (await copy(address)) setCopied(true);
          }}
          sx={{ p: 0.25, color: 'text.disabled' }}
        >
          <Iconify icon={copied ? 'eva:checkmark-fill' : 'eva:copy-outline'} width={14} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};
